import { useState, useEffect, useCallback } from "react";
import {
  PENDING_BOOST_STORAGE_PREFIX,
  STALE_GENERATION_TIMEOUT_MS,
} from "@/config/constants";

interface StoredBoost {
  count: number;
  timestamp: number;
}

interface UsePersistentPendingBoostOptions {
  pipeline: string;
  dbPendingCount: number;
  isDbLoading: boolean;
}

function getStorageKey(pipeline: string) {
  return `${PENDING_BOOST_STORAGE_PREFIX}-${pipeline}`;
}

function readStored(key: string): number {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return 0;
    const parsed: StoredBoost = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > STALE_GENERATION_TIMEOUT_MS) {
      sessionStorage.removeItem(key);
      return 0;
    }
    return parsed.count;
  } catch {
    return 0;
  }
}

function writeStored(key: string, count: number) {
  if (count <= 0) {
    sessionStorage.removeItem(key);
  } else {
    const entry: StoredBoost = { count, timestamp: Date.now() };
    sessionStorage.setItem(key, JSON.stringify(entry));
  }
}

/**
 * Optimistic "pending generation" counter shared by every generation flow.
 *
 * Returns `pendingCount = Math.max(boost, dbPendingCount)`, an `increment`, and
 * a `decrement`. `CreationsGallery` paints `max(0, pendingCount - pendingItems.length)`
 * placeholder cards, so bumping the boost makes a card appear instantly — before
 * the R2 upload / server round-trip / DB row. Once DB rows arrive they become
 * the source of truth and the boost auto-zeroes. State persists in
 * sessionStorage so it survives a mid-generation page refresh.
 *
 * Caller contract (a flow that violates this has a UX bug, not a valid usage):
 *  - Call `increment(n)` SYNCHRONOUSLY before the first `await` in the generate
 *    handler — that is what makes the card paint immediately.
 *  - Call `decrement(n)` on the pre-DB-row failure path (the `catch`) so the
 *    optimistic card disappears on error.
 *  - Destructuring only `{ pendingCount }` (never calling `increment`) means the
 *    card never paints — see the BaseballTrend regression this guards against.
 */
export function usePersistentPendingBoost({
  pipeline,
  dbPendingCount,
  isDbLoading,
}: UsePersistentPendingBoostOptions) {
  const key = getStorageKey(pipeline);

  const [boost, setBoost] = useState<number>(() => readStored(key));

  const increment = useCallback(
    (n: number) => {
      setBoost((prev) => {
        const next = prev + n;
        writeStored(key, next);
        return next;
      });
    },
    [key],
  );

  const decrement = useCallback(
    (n: number) => {
      setBoost((prev) => {
        const next = Math.max(0, prev - n);
        writeStored(key, next);
        return next;
      });
    },
    [key],
  );

  // Once DB has pending rows, it's the source of truth — clear boost
  useEffect(() => {
    if (!isDbLoading && dbPendingCount > 0) {
      setBoost(0);
      sessionStorage.removeItem(key);
    }
  }, [isDbLoading, dbPendingCount, key]);

  const pendingCount = Math.max(boost, dbPendingCount);

  return { pendingCount, increment, decrement };
}
